import React, { useMemo } from 'react';
import { AnalysisModel, CardPenaltyRow, MissingData, PlayerModel, RoundFilter, penaltyStats } from '../../utils/majorAnalysis';
import { seriesColor } from '../../utils/chartPalette';
import { PuttsPenaltiesAsk, shortDate } from './DataNeeded';
import { PlayerCell, RankCell, SortTh, fmt1, sortRows, useSort } from './shared';

interface Props {
  model: AnalysisModel;
  players: PlayerModel[];
  filter: RoundFilter;
  missing: MissingData;
  onOpenRound: (roundId: number) => void;
}

const Penalties: React.FC<Props> = ({ model, players, filter, missing, onOpenRound }) => {
  const { league, dqs, cards, hotSpots } = useMemo(() => penaltyStats(model, filter), [model, filter]);
  const { sort, toggle } = useSort();
  const shown = new Set(players.map((p) => p.player_id));
  const byId = new Map(cards.map((r) => [r.player_id, r]));
  const who = new Map(model.players.map((p) => [p.player_id, p]));
  const anyCardData = cards.some((r) => r.holesWithData > 0);

  const val = (r: CardPenaltyRow | undefined, key: string): number | null => {
    if (!r || !r.holesWithData) return null;
    switch (key) {
      case 'holes': return r.holesWithData;
      case 'strokes': return r.strokes;
      case 'with': return r.holesWithPenalty;
      case 'per18': return r.per18;
      case 'lost': return r.lostOnThoseHoles;
      default: return null;
    }
  };
  const sorted = sortRows(players, sort, (p) => p.order, (p, key) => val(byId.get(p.player_id), key));
  const leagueShown = league.filter((l) => shown.has(l.player_id));
  const dqShown = dqs.filter((d) => shown.has(d.player_id));

  return (
    <div className="ma-tab">
      <section className="ma-card">
        <div className="ma-card-head"><h3 className="ma-h3">League penalties &amp; DQs</h3></div>
        <p className="ma-lede">Penalty strokes assessed on a round (added to the net after the handicap), and disqualifications.</p>
        {leagueShown.length + dqShown.length === 0 ? (
          <p className="ma-foot">None in this major.</p>
        ) : (
          <div className="ma-scroll">
            <table className="tbl tbl--sm ma-table">
              <thead><tr><th className="ma-stick-2">Player</th><th className="c">Round</th><th>Course</th><th className="c">Strokes</th><th>Reason</th></tr></thead>
              <tbody>
                {leagueShown.map((l, i) => {
                  const p = who.get(l.player_id)!;
                  return (
                    <tr key={`p${i}`}>
                      <td className="ma-name-td"><span className="ma-swatch" style={{ background: seriesColor(p.order) }} />{p.name}</td>
                      <td className="c">R{l.round}</td>
                      <td>{l.course} · {shortDate(l.date)}</td>
                      <td className="c ma-strong">+{l.strokes}</td>
                      <td>{l.reason || '—'}</td>
                    </tr>
                  );
                })}
                {dqShown.map((d, i) => {
                  const p = who.get(d.player_id)!;
                  return (
                    <tr key={`d${i}`}>
                      <td className="ma-name-td"><span className="ma-swatch" style={{ background: seriesColor(p.order) }} />{p.name}</td>
                      <td className="c">R{d.round}</td>
                      <td>{d.course} · {shortDate(d.date)}</td>
                      <td className="c ma-strong">DQ</td>
                      <td>{d.reason || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="ma-card">
        <div className="ma-card-head"><h3 className="ma-h3">On-course penalty strokes</h3></div>
        {!anyCardData ? (
          <>
            <p className="ma-lede">No penalty strokes have been entered per hole for this major yet (water, out of bounds, unplayable…).</p>
            <PuttsPenaltiesAsk putts={[]} penalties={missing.penalties} onOpenRound={onOpenRound} show="penalties" />
          </>
        ) : (
          <>
            <p className="ma-lede">
              Penalty strokes marked on the card, hole by hole (already inside the hole score). “Lost on those holes” = strokes
              given up to the field’s average on the holes where a penalty happened (gross).
            </p>
            <div className="ma-scroll">
              <table className="tbl tbl--sm ma-table ma-table--sticky">
                <thead>
                  <tr>
                    <SortTh label="Rank" sortKey="rank" sort={sort} onSort={toggle} className="c ma-stick-1" />
                    <th className="ma-stick-2">Player</th>
                    <SortTh label="Holes" sortKey="holes" sort={sort} onSort={toggle} firstDir={-1} className="c" title="Holes with a penalty entry (0 or more)" />
                    <SortTh label="Penalty strokes" sortKey="strokes" sort={sort} onSort={toggle} className="c" />
                    <SortTh label="Holes with one" sortKey="with" sort={sort} onSort={toggle} className="c" />
                    <SortTh label="Per 18" sortKey="per18" sort={sort} onSort={toggle} className="c" />
                    <SortTh label="Lost on those holes" sortKey="lost" sort={sort} onSort={toggle} className="c" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((p) => {
                    const r = byId.get(p.player_id);
                    const has = !!r && r.holesWithData > 0;
                    return (
                      <tr key={p.player_id}>
                        <RankCell p={p} all={model.players} />
                        <PlayerCell p={p} />
                        <td className="c">{has ? r!.holesWithData : '—'}</td>
                        <td className="c ma-strong">{has ? r!.strokes : '—'}</td>
                        <td className="c">{has ? r!.holesWithPenalty : '—'}</td>
                        <td className="c">{has ? fmt1(r!.per18) : '—'}</td>
                        <td className="c">{has && r!.lostOnThoseHoles != null ? fmt1(r!.lostOnThoseHoles) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hotSpots.length > 0 && (
              <>
                <h4 className="ma-h4">Penalty hot spots</h4>
                <ol className="ma-spots">
                  {hotSpots.slice(0, 6).map((s) => (
                    <li key={`${s.courseKey}:${s.hole}`}>
                      <strong>{s.course} #{s.hole}</strong> — {s.strokes} penalty stroke{s.strokes === 1 ? '' : 's'} by {s.players} player{s.players === 1 ? '' : 's'}
                    </li>
                  ))}
                </ol>
              </>
            )}
            {missing.penalties.length > 0 && (
              <PuttsPenaltiesAsk putts={[]} penalties={missing.penalties} onOpenRound={onOpenRound} show="penalties" />
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default Penalties;
